import { Box, Flex, useBreakpoints } from "@otrium/core";
import { unescape } from "lodash-es";
import { useRouter } from "next/router";
import { forwardRef, useCallback, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
  PromotionDisplayType,
  PromotionType,
  ShopType,
} from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getFileName } from "src/utils/getFileName";
import { PromotionEventType, useSegment } from "src/hooks/useSegment";
import { SEGMENT_IN_VIEW_THRESHOLD, SEGMENT_PROMOTION_TYPE } from "src/segment";
import { usePromotion } from "src/hooks/usePromotion";
import { PromotionalBlock } from "../PromotionalBlock";
import { theme } from "src/theme";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { PromotionBlockSkeleton } from "src/organisms/ProductList/PromotionBlockSkeleton";
import Link from "next/link";

interface BrandsPromoProps {
  skipTrackingViewed?: boolean;
  slug: string;
  promotionType?: PromotionType;
  callback?: (loading: boolean) => void;
}

interface PromoBannerImageProps {
  imageSrc: string;
  message?: string | null;
}

const PromoBannerImage = forwardRef<HTMLImageElement, PromoBannerImageProps>(
  ({ imageSrc, message }, ref) => (
    <img
      ref={ref}
      src={imageSrc}
      alt={message || "Promotion banner"}
      className="lazyload"
      style={{ minHeight: theme.space.space96 }}
    />
  )
);

PromoBannerImage.displayName = "PromoBannerImage";

const BrandsPromo = ({
  skipTrackingViewed,
  slug,
  promotionType,
  callback,
}: BrandsPromoProps): JSX.Element | null => {
  const { isDesktop } = useBreakpoints();
  const router = useRouter();
  const { slug: genderId } = router.query;
  const localeGenderId =
    typeof genderId === "string" ? localeFix({ gender: genderId }) : "women";
  const { data: promoData, loading } = usePromotion(
    slug,
    promotionType || PromotionType.Category,
    (localeGenderId as ShopType) || ShopType.Women
  );
  const isLoggedIn = useIsLoggedIn();
  const { segmentPromotionClickedOrViewed } = useSegment();
  const { enableGatedHomepage } = useFeatureFlags();

  const promotion = promoData?.promotion;

  const getPromoByGender = useCallback(() => {
    switch (localeGenderId) {
      case "women":
        return promotion?.promo_url_women;
      case "men":
        return promotion?.promo_url_men;
      default:
        return promotion?.promo_url_kids;
    }
  }, [promotion, localeGenderId]);

  const promoBannerUrl = unescape(getPromoByGender() || "");

  const trackingData = useMemo(() => {
    return {
      name: promotion?.promo_name,
      creative: getFileName(promoBannerUrl),
      position: "plp-page-promo-banner",
      promotionType: SEGMENT_PROMOTION_TYPE.plpPromoBanner,
      gated: !!(!isLoggedIn && enableGatedHomepage),
    };
  }, [promoBannerUrl, promotion, isLoggedIn, enableGatedHomepage]);

  const promotionClickedOrViewed = useCallback(
    (eventType: PromotionEventType) => {
      void segmentPromotionClickedOrViewed({
        ...trackingData,
        eventType,
      });
    },
    [trackingData, segmentPromotionClickedOrViewed]
  );

  const { ref: promoBannerRef } = useInView({
    triggerOnce: true,
    skip: skipTrackingViewed,
    threshold: SEGMENT_IN_VIEW_THRESHOLD,
    onChange(inView) {
      if (inView) {
        promotionClickedOrViewed(PromotionEventType.view);
      }
    },
  });

  const promoClickHandler = useCallback(() => {
    promotionClickedOrViewed(PromotionEventType.click);
  }, [promotionClickedOrViewed]);

  useEffect(() => {
    if (callback && !loading && (!promoData || !promoData?.promotion)) {
      callback(false);
    }
  }, [callback, loading, promoData]);

  if (loading) {
    return <PromotionBlockSkeleton />;
  }

  return promotion ? (
    <>
      <Box
        display={
          isDesktop
            ? ["none", "none", "block"]
            : ["block", "block", "none", "none", "none"]
        }
      >
        {!isDesktop && (
          <>
            {promotion.promo_image_mobile && (
              <Flex
                data-testid="promo-image-mobile"
                mb={2}
                flexWrap="wrap"
                flexDirection="column"
                gap={theme.space.space8}
              >
                {promoBannerUrl ? (
                  <a
                    href={promoBannerUrl}
                    onClick={promoClickHandler}
                    ref={promoBannerRef}
                  >
                    <PromoBannerImage
                      imageSrc={promotion.promo_image_mobile}
                      message={promotion.promo_message}
                    />
                  </a>
                ) : (
                  <PromoBannerImage
                    ref={promoBannerRef}
                    imageSrc={promotion.promo_image_mobile}
                    message={promotion.promo_message}
                  />
                )}
              </Flex>
            )}
            {promotion.promo_display_type !== PromotionDisplayType.FlashSale ? (
              <Flex justifyContent="center">
                <PromotionalBlock promotion={promotion || {}} />
              </Flex>
            ) : null}
          </>
        )}

        {isDesktop && (
          <Flex
            mb={2}
            flexWrap="wrap"
            flexDirection="column"
            gap={theme.space.space8}
            data-testid="promo-image-desktop"
          >
            {promotion.promo_image_desktop &&
              (promoBannerUrl ? (
                <Link
                  href={promoBannerUrl}
                  onClick={promoClickHandler}
                  ref={promoBannerRef}
                >
                  <PromoBannerImage
                    imageSrc={promotion.promo_image_desktop}
                    message={promotion.promo_message}
                  />
                </Link>
              ) : (
                <PromoBannerImage
                  ref={promoBannerRef}
                  imageSrc={promotion.promo_image_desktop}
                  message={promotion.promo_message}
                />
              ))}
            {promotion.promo_display_type !== PromotionDisplayType.FlashSale ? (
              <Flex justifyContent="center">
                <PromotionalBlock promotion={promotion || {}} />
              </Flex>
            ) : null}
          </Flex>
        )}
      </Box>
    </>
  ) : null;
};

export default BrandsPromo;
